import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";

type Slot = {
  staff_id: string;
  start_at: string;
  end_at: string;
  score: number;
  factors: Record<string, unknown>;
};

type AvailabilityData = { items: Slot[]; meta: { total: number } };

function isoDate(d: Date) {
  return d.toISOString().split("T")[0];
}

function buildDateStrip(anchor: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i <= 6; i++) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

export function TgReschedulePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const dayStr = isoDate(selectedDay);

  const { data, isLoading } = useQuery<AvailabilityData>({
    queryKey: ["tg-reschedule-avail", id, dayStr],
    queryFn: () => apiGet(`/v3/tg/appointments/${id}/availability?day=${dayStr}`),
    enabled: !!id,
  });

  const reschedule = useMutation({
    mutationFn: (new_start_at: string) =>
      apiPost(`/v3/tg/appointments/${id}/transition`, { action: "reschedule", new_start_at }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tg-appointment", id] });
      qc.invalidateQueries({ queryKey: ["tg-today"] });
      navigate(`/tg/appointments/${id}`);
    },
  });

  const strip = buildDateStrip(new Date());

  return (
    <div className="p-4 space-y-4">
      <button onClick={() => navigate(-1)} className="text-teal-600 text-sm">← Back</button>
      <h1 className="text-lg font-bold text-gray-900">Reschedule</h1>

      {/* Date strip */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {strip.map((d) => {
          const active = isoDate(d) === dayStr;
          return (
            <button
              key={isoDate(d)}
              onClick={() => { setSelectedDay(d); setSelectedSlot(null); }}
              className={cn(
                "flex-shrink-0 flex flex-col items-center px-3 py-1.5 rounded-xl text-xs transition-colors",
                active ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600"
              )}
            >
              <span>{d.toLocaleDateString([], { weekday: "short" })}</span>
              <span className="font-bold">{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {isLoading && <div className="text-gray-400 text-center py-6">Checking availability…</div>}

      {!isLoading && (data?.items ?? []).length === 0 && (
        <div className="text-gray-400 text-center py-8">No slots available for this day.</div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {(data?.items ?? []).map((slot) => {
          const time = new Date(slot.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          const active = selectedSlot === slot.start_at;
          return (
            <button
              key={slot.start_at}
              onClick={() => setSelectedSlot(slot.start_at)}
              className={cn(
                "py-2 px-2 rounded-xl text-sm border transition-colors text-center",
                active
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white border-gray-200 text-gray-700"
              )}
            >
              {time}
            </button>
          );
        })}
      </div>

      {selectedSlot && (
        <button
          onClick={() => reschedule.mutate(selectedSlot)}
          disabled={reschedule.isPending}
          className="w-full bg-teal-600 text-white py-3 rounded-xl text-base font-semibold disabled:opacity-50"
        >
          {reschedule.isPending ? "Rescheduling…" : "Confirm reschedule"}
        </button>
      )}

      {reschedule.isError && (
        <p className="text-red-500 text-sm text-center">{String(reschedule.error)}</p>
      )}
    </div>
  );
}
